"use client";

import { usePathname } from "next/navigation";
import { useEffect } from "react";

/**
 * Preline-JS-Lader (Plan 2026-10-04, Regel 10): startet die Preline-Plugins
 * (Dropdown, Overlay/Offcanvas, Collapse, Tooltip …) nach jedem Seitenwechsel
 * neu. Vorgehen exakt nach Preline-Doku „framework_documentation nextjs":
 * `preline/non-auto`, `cleanCollection()` vor `autoInit()` bei jeder
 * Pfadänderung. Keine optionalen Fremdbibliotheken (Datatable, Datepicker,
 * Range Slider, File Upload werden in diesem Schnitt nicht genutzt).
 */
interface OverlayInstanz {
  element: { el: HTMLElement; destroy: () => void; accessibilityComponent?: unknown };
}
interface TastaturBeobachter {
  unregisterComponent: (komponente: unknown) => void;
}

/**
 * Baut alle bekannten Overlay-Instanzen mit Preline-`destroy()` ab (Listener, Backdrop, Sammlung).
 * `destroy()` nimmt auch die Klasse `hidden` vom Element; ein vorher verborgenes Overlay bleibt verborgen.
 */
function baueOverlayInstanzenAb() {
  const sammlung = (window as unknown as { $hsOverlayCollection?: OverlayInstanz[] }).$hsOverlayCollection;
  if (!Array.isArray(sammlung)) return;
  const beobachter = (window as unknown as { HSAccessibilityObserver?: TastaturBeobachter }).HSAccessibilityObserver;
  for (const { element } of [...sammlung]) {
    const verborgen = element.el.classList.contains("hidden");
    // destroy() meldet die Tastatur-Registrierung (Escape) nicht ab; eine übrig gebliebene
    // Registrierung der alten Instanz würde Escape am neuen Overlay verschlucken.
    if (element.accessibilityComponent) beobachter?.unregisterComponent(element.accessibilityComponent);
    element.destroy();
    if (verborgen) element.el.classList.add("hidden");
  }
  document.body.style.overflow = "";
}

interface AuswahlInstanz {
  element: { destroy: () => void };
}

/**
 * Baut die erweiterten Auswahlfelder (`HSSelect`, Formular-Bausteine) vor dem Neuaufbau ab. Wie bei den Overlays
 * leert `cleanCollection()` nur die Sammlung; ein bereits aufgebautes Auswahlfeld bekäme beim folgenden
 * `autoInit()` einen zweiten Auslöser neben den ersten. `destroy()` entfernt Auslöser und Liste und stellt das
 * native (versteckte) Feld wieder her; `autoInit()` baut es danach genau einmal neu auf.
 */
function baueAuswahlInstanzenAb() {
  const sammlung = (window as unknown as { $hsSelectCollection?: AuswahlInstanz[] }).$hsSelectCollection;
  if (!Array.isArray(sammlung)) return;
  for (const { element } of [...sammlung]) {
    try {
      element.destroy();
    } catch (fehler) {
      // Ein bereits ausgehängtes Feld (React hat es entfernt) lässt sich nicht mehr abbauen; die Sammlung wird ohnehin geleert.
      console.error("PrelineClient: Auswahlfeld konnte nicht abgebaut werden", fehler);
    }
  }
}

export function PrelineClient() {
  const pathname = usePathname();

  useEffect(() => {
    let cancelled = false;

    void import("preline/non-auto").then(({ HSStaticMethods }) => {
      if (cancelled) return;

      // cleanCollection() leert nur die Sammlung, nimmt aber die Klick-Listener der alten
      // Overlay-Instanzen nicht von den Schaltflächen (data-hs-overlay). Eine vom Baustein
      // `usePrelineOverlay` zuvor angelegte Instanz bliebe so neben der neuen bestehen: ein Klick
      // auf "Schließen" schließt (alte Instanz) und öffnet sofort wieder (neue). Darum erst abbauen.
      baueOverlayInstanzenAb();
      baueAuswahlInstanzenAb();
      HSStaticMethods.cleanCollection();
      HSStaticMethods.autoInit();
    });

    return () => {
      cancelled = true;
    };
  }, [pathname]);

  return null;
}
